// Helper function to get months between two dates
export const getMonthsBetween = (start, end) => {
  // Validate input dates
  if (!start || !end) {
    return [];
  }

  // Parse the input dates
  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");

  // Check if dates are valid
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return [];
  }

  const months = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const monthStr = `${year}-${month}`;

    months.push(monthStr);

    // Move to first day of next month
    currentDate = new Date(year, currentDate.getMonth() + 1, 1);
  }

  return months;
};

// Helper function to get weeks between two dates
export const getWeeksBetween = (start, end) => {
  // Validate input dates
  if (!start || !end) {
    console.log("Invalid dates provided for weeks, returning empty array");
    return [];
  }

  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");
  
  // Check if dates are valid
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    console.log("Invalid date objects created for weeks, returning empty array");
    return [];
  }
  
  const weeks = [];
  let currentDate = new Date(startDate);
  
  // Move to the start of the week (Monday)
  const dayOfWeek = currentDate.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  currentDate.setDate(currentDate.getDate() - daysToMonday);
  
  while (currentDate <= endDate) {
    const weekStart = new Date(currentDate);
    const weekEnd = new Date(currentDate);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    weeks.push({
      start: weekStart.toISOString().split('T')[0],
      end: weekEnd.toISOString().split('T')[0],
      display: `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`
    });
    
    // Move to next week
    currentDate.setDate(currentDate.getDate() + 7);
  }
  
  return weeks;
};

// Helper function to group weeks into blocks of a given duration
export const getWeeklyGroups = (start, end, groupSize) => {
  if (!start || !end || !groupSize || groupSize <= 0) return [];
  const weeks = getWeeksBetween(start, end);
  const groups = [];
  for (let i = 0; i < weeks.length; i += groupSize) {
    const groupWeeks = weeks.slice(i, i + groupSize);
    if (groupWeeks.length === 0) continue;
    groups.push({
      start: groupWeeks[0].start,
      end: groupWeeks[groupWeeks.length - 1].end,
      weeks: groupWeeks,
    });
  }
  return groups;
};

// Helper to get Monday (week start) for a given date (Date or 'YYYY-MM-DD')
export const getWeekStartForDate = (dateInput) => {
  let date;
  if (typeof dateInput === 'string') {
    const [y, m, d] = dateInput.split('-').map(Number);
    date = new Date(y, m - 1, d);
  } else if (dateInput instanceof Date) {
    date = new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
  } else {
    return null;
  }
  const day = date.getDay();
  const daysToMonday = day === 0 ? 6 : day - 1; // Sunday => 6, Mon(1) => 0
  const monday = new Date(date);
  monday.setDate(monday.getDate() - daysToMonday);
  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, '0');
  const dayStr = String(monday.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayStr}`;
};

// Helper to get the week-start keys for the group containing a given week key
export const getGroupWeekStarts = (scheduleData, weekKey) => {
  if (!scheduleData || !scheduleData.startDate || !scheduleData.endDate) return [weekKey];
  const duration = scheduleData.weekDuration || 1;
  const allWeeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
  for (let i = 0; i < allWeeks.length; i += duration) {
    const group = allWeeks.slice(i, i + duration);
    if (group.some((w) => w.start === weekKey)) {
      return group.map((w) => w.start);
    }
  }
  return [weekKey];
};

// Helper function to get all months from schedules
export const getAllMonths = (schedules) => {
  const allMonths = new Set();
  Object.values(schedules).forEach((scheduleData) => {
    // Get months from the schedule's date range
    if (scheduleData.startDate && scheduleData.endDate) {
      const months = getMonthsBetween(scheduleData.startDate, scheduleData.endDate);
      months.forEach(month => allMonths.add(month));
    }
  });
  return Array.from(allMonths).sort();
};

// Function to get weekends for a specific month
export const getWeekendsForMonth = (yearMonth) => {
  if (!yearMonth) return [];

  const [year, month] = yearMonth.split("-");
  const weekends = [];
  const date = new Date(year, month - 1, 1);

  while (date.getMonth() === month - 1) {
    if (date.getDay() === 6) {
      // Saturday
      const saturday = new Date(date);
      const sunday = new Date(date);
      sunday.setDate(sunday.getDate() + 1);
      
      // Format dates as YYYY-MM-DD in local time to avoid timezone issues
      const saturdayStr = `${saturday.getFullYear()}-${String(saturday.getMonth() + 1).padStart(2, "0")}-${String(saturday.getDate()).padStart(2, "0")}`;
      const sundayStr = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, "0")}-${String(sunday.getDate()).padStart(2, "0")}`;
      
      weekends.push({
        saturday: saturdayStr,
        sunday: sundayStr,
      });
    }
    date.setDate(date.getDate() + 1);
  }
  return weekends;
};

// Function to get all dates between start and end dates
export const getAllDates = (schedules) => {
  const allDates = new Set(); // Use Set to prevent duplicates
  Object.values(schedules).forEach((scheduleData) => {
    // Check if dates exist and are valid
    if (!scheduleData.startDate || !scheduleData.endDate) {
      return;
    }

    try {
      // Parse the dates explicitly using YYYY-MM-DD format
      const [startYear, startMonth, startDay] = scheduleData.startDate
        .split("-")
        .map(Number);
      const [endYear, endMonth, endDay] = scheduleData.endDate
        .split("-")
        .map(Number);

      // Create dates using local timezone
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);

      // Validate dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return;
      }

      const current = new Date(start);

      while (current <= end) {
        // Format date as YYYY-MM-DD
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, "0");
        const day = String(current.getDate()).padStart(2, "0");
        const dateStr = `${year}-${month}-${day}`;

        allDates.add(dateStr);
        current.setDate(current.getDate() + 1);
      }
    } catch (error) {
      console.error("Error processing dates:", error);
    }
  });
  return Array.from(allDates).sort(); // Convert Set to sorted array
};

